import type { SendRequestType } from '@/lib/utils/shared/httpTools'
import httpTools from '@/lib/utils/shared/httpTools'

type HydraGetOrderDetailsProps = {
  hydraToken: string
  orderHref: string
}

type TaxItem = {
  type: string
  amount: number
  percentage: number
}

type AggregateTax = {
  items: TaxItem[]
  amount: number
}

type Terms = {
  discount?: {
    amount: number
  }
  period?: string
  pretotal?: number
  tax?: {
    amount: number
    percentage: number
    aggregate?: AggregateTax
  }
  freight?: {
    amount: number
    terms?: {
      tax?: {
        aggregate?: AggregateTax
      }
    }
  }
  priceEach?: number
  pvEach?: number
  taxablePriceEach?: number
  subtotal?: number
  pv?: number
  taxableTotal?: number
  total?: number
}

export type HydraAddress = {
  address1: string
  address2?: string
  city: string
  state: string
  zip?: string
  country: string
}

type Name = {
  firstName: string
  lastName: string
  fullName: string
  'fullName@en'?: string
}

type PaymentMethodDetails = {
  payer: string
  bankName: string
  creditCardNumberMasked: string
  creditCardNetwork: string
}

export type OrderDetailsTransactionItem = {
  billToAddress: HydraAddress
  billToName: Name
  billToPhone: string
  methodDetails: PaymentMethodDetails
  amount: number
  authorization: string
  type: string
  method: string
  billToEmail: string
}

type Transactions = {
  items: OrderDetailsTransactionItem[]
  aggregate: {
    amount: number
  }
}

type Creator = {
  humanName: {
    fullName: string
  }
}

type Customer = {
  id: {
    unicity: string
  }
  href: string
}

type Invoice = {
  id: {
    unicity: string
  }
  href: string
}

type Item = {
  id: {
    unicity: string
  }
  href: string
}

type CatalogSlide = {
  content: {
    description: string
  }
}

type LineItem = {
  item: Item
  catalogSlide: CatalogSlide
  terms: Terms
  quantity: number
  quantityDetails: {
    quantityBackordered: number
  }
}

type Lines = {
  items: LineItem[]
}

export type HydraGetOrderDetailsShipment = {
  trackingNumberHref: string
  trackingNumber: string
}

export type HydraGetOrderDetailsResponse = {
  shipments?: {
    items: HydraGetOrderDetailsShipment[]
  }
  terms: Terms
  transactions: Transactions
  creator: Creator
  currency: string
  customer: Customer
  fulfillmentStatus: string
  invoice: Invoice
  lines: Lines
  market: string
  dateCreated: string
  id: {
    unicity: string
    label: string
    unity: string
  }
  notes: string
  paymentStatus: string
  shipToAddress: HydraAddress
  shipToEmail: string
  shippingMethod: {
    type: string
    location: string
  }
  shipToName: Name
  shipToPhone: string
  source: {
    medium: string
    agent: string
  }
  type: string
  taxedAs: string
  originalOrder: {
    id: {
      unicity: string | null
    }
  }
  availableActions: string[]
  href: string
}

export const HydraGetOrderDetails = ({
  hydraToken,
  orderHref,
}: HydraGetOrderDetailsProps) => {
  const request: SendRequestType = {
    method: 'GET',
    url: `/orders/${orderHref}?expand=customer,lines`,
    token: hydraToken,
  }

  return httpTools.sendRequest<HydraGetOrderDetailsResponse>(request)
}
